import { useState } from "react";
import { MapPin, Phone, Mail, MessageCircle } from "lucide-react";
import client, { formatApiError } from "../lib/api";
import { trackEvent } from "../lib/tracking";
import SEO from "../components/SEO";
import Divider from "../components/Divider";
import { Reveal } from "../components/Reveal";

const REASONS = ["Corsi", "Collaborazioni", "Ospite podcast", "Altro"];

export default function Contatti() {
  const [form, setForm] = useState({ name: "", email: "", reason: "Corsi", message: "", privacy: false });
  const [state, setState] = useState({ loading: false, done: false, error: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      setState({ loading: false, done: false, error: "Devi accettare la Privacy Policy." });
      return;
    }
    setState({ loading: true, done: false, error: "" });
    try {
      await client.post("/contacts", form);
      trackEvent("contact_submit", { reason: form.reason });
      setState({ loading: false, done: true, error: "" });
    } catch (err) {
      setState({ loading: false, done: false, error: formatApiError(err) });
    }
  };

  const inputCls = "w-full rounded-lg px-4 py-3.5 bg-nero/40 border border-panna/20 text-panna placeholder:text-panna/40 focus:outline-none focus:ring-2 focus:ring-oro/60";

  return (
    <div data-testid="contatti-page">
      <SEO title="Contatti" path="/contatti"
           description="Contatta Stefano Cremonese: corsi, collaborazioni, ospitate al podcast. Castel d'Azzano (Verona)." />
      <section className="bg-nero pt-40 pb-20 grain relative">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 text-center">
          <Reveal>
            <p className="text-oro text-xs uppercase tracking-[0.35em] mb-4">Parliamone</p>
            <h1 className="font-serif text-4xl sm:text-5xl font-bold text-panna">Contatti</h1>
            <Divider light className="mt-10" />
          </Reveal>
        </div>
      </section>
      <section className="bg-nero pb-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-14">
          <Reveal>
            <div className="space-y-6">
              {[
                { Icon: Mail, label: "Email", value: "info@dentroilcalice.it", href: "mailto:info@dentroilcalice.it", testId: "contact-email" },
                { Icon: Phone, label: "Telefono", value: "+39 348 2618 512", href: "tel:+393482618512", testId: "contact-phone" },
                { Icon: MessageCircle, label: "WhatsApp", value: "Scrivimi direttamente", href: "https://wa.me/393482618512", testId: "contact-whatsapp" },
              ].map(({ Icon, label, value, href, testId }) => (
                <a key={label} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
                   data-testid={testId}
                   className="flex items-center gap-5 rounded-2xl border border-panna/10 bg-nero-light p-6 hover:border-oro/40 transition-colors">
                  <div className="h-12 w-12 rounded-full border border-oro/40 flex items-center justify-center shrink-0">
                    <Icon size={19} className="text-oro" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-panna/50">{label}</p>
                    <p className="text-panna font-medium mt-0.5">{value}</p>
                  </div>
                </a>
              ))}
              <div className="flex items-start gap-5 rounded-2xl border border-panna/10 bg-nero-light p-6">
                <div className="h-12 w-12 rounded-full border border-oro/40 flex items-center justify-center shrink-0">
                  <MapPin size={19} className="text-oro" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-panna/50">Dove</p>
                  <p className="text-panna font-medium mt-0.5">Piazza J.F. Kennedy 2<br />37060 Castel d'Azzano (VR)</p>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden border border-panna/10">
                <iframe title="Mappa — Castel d'Azzano (VR)" data-testid="contact-map"
                        src="https://www.openstreetmap.org/export/embed.html?bbox=10.93%2C45.33%2C11.02%2C45.37&layer=mapnik&marker=45.3499%2C10.9534"
                        className="w-full h-56 opacity-90" loading="lazy" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            {state.done ? (
              <div data-testid="contact-success" className="rounded-2xl border border-oliva/40 bg-oliva/10 p-10 text-center">
                <p className="font-serif text-2xl text-panna">Messaggio inviato.</p>
                <p className="mt-3 text-panna/70">Ti risponderò personalmente il prima possibile. Intanto, grazie.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl border border-panna/10 bg-nero-light p-8 sm:p-10 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <input type="text" required minLength={2} placeholder="Nome e cognome" data-testid="contact-name"
                         value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                  <input type="email" required placeholder="Email" data-testid="contact-email-input"
                         value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-panna/50 mb-3">Motivo</p>
                  <div className="flex flex-wrap gap-2.5" data-testid="contact-reason">
                    {REASONS.map((r) => (
                      <button type="button" key={r} data-testid={`reason-${r.toLowerCase().replace(/\s+/g, "-")}`}
                              onClick={() => setForm({ ...form, reason: r })}
                              className={`rounded-full px-4 py-2 text-sm border transition-colors ${
                                form.reason === r ? "bg-bordeaux text-panna border-bordeaux" : "border-panna/20 text-panna/70 hover:border-oro/50"
                              }`}>
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea required minLength={5} rows={5} placeholder="Il tuo messaggio" data-testid="contact-message"
                          value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} />
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" data-testid="contact-privacy" checked={form.privacy}
                         onChange={(e) => setForm({ ...form, privacy: e.target.checked })} className="mt-1 h-4 w-4 accent-[#B0832F]" />
                  <span className="text-xs text-panna/65 leading-relaxed">
                    Ho letto e accetto la <a href="/privacy-policy" className="underline text-oro">Privacy Policy</a> e
                    acconsento al trattamento dei dati per ricevere una risposta.
                  </span>
                </label>
                {state.error && <p data-testid="contact-error" className="text-sm text-oro-light">{state.error}</p>}
                <button type="submit" data-testid="contact-submit" disabled={state.loading}
                        className="w-full sm:w-auto rounded-full bg-bordeaux px-10 py-4 font-medium text-panna border border-oro/30 hover:bg-bordeaux-dark transition-colors disabled:opacity-60">
                  {state.loading ? "Invio in corso…" : "Invia il messaggio"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
